import React, { useCallback, useEffect, useRef, useState } from 'react';
import Scene3D from '../components/Scene3D';
import Sidebar from '../components/Sidebar';
import CostCalculator from '../components/CostCalculator';
import RotationToolbar from '../components/RotationToolbar';
import BottomToolbar from '../components/BottomToolbar';
import { Loader2, ChevronDown, Trash2, ClipboardList } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { getSharedProject, getElements, getStones, getCategories, seedData } from '../api';
import useStore from '../store/useStore';
import { applyQuizState, readQuizFromURL, isEmbedMode } from '../lib/quizApply';
import { VARIANT, isPamyatniki } from '../lib/variant';

const formatPriceKZ = (price) => new Intl.NumberFormat('kk-KZ', { style: 'decimal', maximumFractionDigits: 0 }).format(price || 0);

const CATEGORY_BADGE = {
  monument:    'bg-rose-100 text-rose-700',
  pedestal:    'bg-orange-100 text-orange-700',
  flowerbed:   'bg-emerald-100 text-emerald-700',
  bench:       'bg-cyan-100 text-cyan-700',
  table:       'bg-indigo-100 text-indigo-700',
  accessories: 'bg-violet-100 text-violet-700',
  engraving:   'bg-fuchsia-100 text-fuchsia-700',
  fence:       'bg-green-100 text-green-700',
};

// Per-element price for breakdown display (mirrors CostCalculator's getElementPrice).
function getElementPriceForBreakdown(el) {
  const priceUnit = el.price_unit || 'piece';
  const basePrice = el.price || 0;
  if (priceUnit === 'cubic_meter') {
    const w = el.width || 1, h = el.height || 1, d = el.depth || 1;
    const isPShape = el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle';
    const volume = isPShape ? 0.08 * h * (2 * d + w) : w * h * d;
    // Плоская доплата за форму (поле installation_price) — чтобы строка разбивки,
    // уходящая в лендинг-квиз, совпадала с итогом (см. useStore.getTotalPrice).
    return Math.round(basePrice * volume) + (isPamyatniki ? (el.installation_price || 0) : 0);
  }
  if (priceUnit === 'linear_meter') return Math.round(basePrice * (el.width || 1));
  if (priceUnit === 'sqmeter')      return Math.round(basePrice * (el.width || 1) * (el.depth || 1));
  return Math.round(basePrice);
}

function getBorderPriceForBreakdown(border, perimeterM) {
  if (!border) return 0;
  const unit = border.price_unit || 'linear_meter';
  const basePrice = Number(border.price) || 0;
  if (unit === 'cubic_meter') {
    const h = Number(border.height) || 0.1;
    const t = Number(border.pricing_thickness_m) || 0.02;
    return Math.round(basePrice * perimeterM * h * t);
  }
  return Math.round(basePrice * perimeterM);
}

function EmbedPriceChip() {
  const [expanded, setExpanded] = useState(false);
  const [installationExpanded, setInstallationExpanded] = useState(false);
  const [excludedInstallItems, setExcludedInstallItems] = useState(() => new Set());
  const total = useStore((s) => {
    void s.placedElements; void s.selectedFloor; void s.selectedFoundation;
    void s.selectedFence; void s.selectedBorder; void s.customItems;
    void s.perimeterWidth; void s.perimeterLength; void s.flowerbedFilling;
    return s.getTotalPrice();
  });
  const installationPrice = useStore((s) => {
    void s.placedElements; void s.selectedFloor; void s.selectedFoundation;
    void s.selectedFence; void s.selectedBorder;
    void s.perimeterWidth; void s.perimeterLength;
    return s.getTotalInstallationPrice();
  });
  const placedElements   = useStore((s) => s.placedElements);
  const selectedFloor    = useStore((s) => s.selectedFloor);
  const selectedFoundation = useStore((s) => s.selectedFoundation);
  const selectedFence    = useStore((s) => s.selectedFence);
  const selectedBorder   = useStore((s) => s.selectedBorder);
  const customItems      = useStore((s) => s.customItems);
  const flowerbedFilling = useStore((s) => s.flowerbedFilling);
  const categories       = useStore((s) => s.categories);
  const perimeterWidth   = useStore((s) => s.perimeterWidth);
  const perimeterLength  = useStore((s) => s.perimeterLength);

  const plotArea = perimeterWidth * perimeterLength;
  const perimeterM = 2 * (perimeterWidth + perimeterLength);
  const itemsCount = placedElements.length
    + (selectedFloor ? 1 : 0)
    + (selectedFoundation ? 1 : 0)
    + (selectedBorder ? 1 : 0)
    + (selectedFence ? 1 : 0)
    + (customItems?.length || 0);

  // Installation breakdown — mirrors CostCalculator.getInstallationBreakdown so the
  // embed chip can show the same per-line items with delete buttons.
  const getInstallationBreakdown = () => {
    const items = [];
    const getCat = (name) => categories?.find(c => c.name === name);
    const getCatById = (id) => categories?.find(c => c.id === id);
    if (selectedFoundation) {
      const cat = getCat('foundation');
      const price = (cat?.installation_price || 0) * plotArea;
      if (price > 0) items.push({ label: 'Фундамент', price });
    }
    if (selectedFloor) {
      const installPerM2 = selectedFloor.installation_price || 0;
      const price = installPerM2 * plotArea;
      if (price > 0) items.push({ label: 'Напольное покрытие (укладка)', price });
    }
    if (selectedBorder) {
      const cat = getCat('border');
      const price = (cat?.installation_price || 0) * perimeterM;
      if (price > 0) items.push({ label: 'Бордюр', price });
    }
    if (selectedFence) {
      const cat = getCat('fence');
      const price = (cat?.installation_price || 0) * perimeterM;
      if (price > 0) items.push({ label: 'Оградка', price });
    }
    const grouped = {};
    placedElements.forEach(el => {
      const cat = getCatById(el.category_id);
      const installPrice = cat?.installation_price || 0;
      if (installPrice > 0 && cat) {
        const key = cat.id;
        if (!grouped[key]) grouped[key] = { label: cat.name_ru, count: 0, unitPrice: installPrice };
        grouped[key].count += 1;
      }
    });
    Object.values(grouped).forEach(({ label, count, unitPrice }) => {
      items.push({ label, count, price: unitPrice * count });
    });
    return items;
  };
  const installationBreakdown = getInstallationBreakdown();
  const installationVisibleTotal = isPamyatniki ? 0 : installationBreakdown
    .filter(it => !excludedInstallItems.has(it.label))
    .reduce((s, it) => s + it.price, 0);
  const excludedInstallTotal = installationBreakdown
    .filter(it => excludedInstallItems.has(it.label))
    .reduce((s, it) => s + it.price, 0);
  // Памятники: монтаж не выделяется отдельной строкой и не входит в итог.
  const adjustedTotal = total - excludedInstallTotal - (isPamyatniki ? installationPrice : 0);

  // Embed (?embed=1): шлём родителю (мокап квиза на imperial-ritual.kz) актуальную
  // цену с монтажом. Внешняя страница использует это для бейджа в шапке, цены на
  // финальном экране и подстановки в WhatsApp-сообщение. Без этого мокап не видит
  // сумму вообще — CostCalculator (где раньше была эта логика) в embed не рендерится.
  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return;
    // Памятники: финальный экран квиза показывает построчный прайс — для blago
    // ветка ниже DCE'ится терсером (isPamyatniki = false на этапе сборки) и payload
    // остаётся прежним.
    if (isPamyatniki) {
      // `category` — стабильный английский ключ (monument/pedestal/engraving/...),
      // им матчится квиз-мок на imperial-ritual.kz, чтобы цены в шаге размеров
      // совпадали с конструктором и не ломались при ребрендинге name_ru.
      // `categoryLabel` оставлен для отображения в финальной спецификации.
      const breakdown = [];
      if (selectedFoundation) {
        breakdown.push({
          category: 'foundation',
          label: selectedFoundation.name_ru,
          price: Math.round((selectedFoundation.price || 0) * plotArea),
          categoryLabel: 'Фундамент',
        });
      }
      if (selectedFloor) {
        breakdown.push({
          category: 'floor',
          label: selectedFloor.name_ru,
          price: Math.round((selectedFloor.price || 0) * plotArea),
          categoryLabel: 'Покрытие',
        });
      }
      if (selectedBorder) {
        breakdown.push({
          category: 'border',
          label: selectedBorder.name_ru,
          price: getBorderPriceForBreakdown(selectedBorder, perimeterM),
          categoryLabel: 'Бордюр',
        });
      }
      if (selectedFence) {
        breakdown.push({
          category: 'fence',
          label: selectedFence.name_ru,
          price: Math.round((selectedFence.price || 0) * perimeterM),
          categoryLabel: 'Оградка',
        });
      }
      placedElements.forEach((el) => {
        const cat = categories?.find(c => c.id === el.category_id);
        breakdown.push({
          category: cat?.name || 'item',
          label: el.name_ru || el.name || '',
          price: getElementPriceForBreakdown(el),
          categoryLabel: cat?.name_ru || 'Элемент',
        });
      });
      if (flowerbedFilling) {
        const innerPrice = placedElements.reduce((sum, el) => {
          if (el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle') {
            const innerW = Math.max(0, (el.width || 1) - 0.16);
            const innerD = Math.max(0, (el.depth || 1) - 0.08);
            return sum + (Number(flowerbedFilling.price) || 0) * innerW * innerD;
          }
          return sum;
        }, 0);
        if (innerPrice > 0) {
          breakdown.push({
            category: 'flowerbed_filling',
            label: flowerbedFilling.name_ru,
            price: Math.round(innerPrice),
            categoryLabel: 'Наполнение',
          });
        }
      }
      (customItems || []).forEach((it) => {
        breakdown.push({
          category: 'custom',
          label: it.name,
          price: Math.round(it.price || 0),
          categoryLabel: 'Доп.',
        });
      });
      try {
        window.parent.postMessage({
          type: 'iri:price',
          price: adjustedTotal,
          installationPrice: installationVisibleTotal,
          materialPrice: adjustedTotal - installationVisibleTotal,
          elementCount: itemsCount,
          areaM2: plotArea,
          perimeterM,
          breakdown,
        }, '*');
      } catch (e) { /* cross-origin / sandbox — игнорим */ }
      return;
    }
    try {
      window.parent.postMessage({
        type: 'iri:price',
        price: adjustedTotal,
        installationPrice: installationVisibleTotal,
        materialPrice: adjustedTotal - installationVisibleTotal,
        elementCount: itemsCount,
        areaM2: plotArea,
        perimeterM,
      }, '*');
    } catch (e) { /* cross-origin / sandbox — игнорим */ }
  }, [adjustedTotal, installationVisibleTotal, itemsCount, plotArea, perimeterM]);

  return (
    <div className="absolute top-3 right-3 z-20 max-w-[280px]">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1.5 rounded-lg shadow-md border border-slate-200 text-sm font-semibold text-slate-900 hover:bg-white"
      >
        <span className="flex-1 text-right">{formatPriceKZ(adjustedTotal)} ₸</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-1 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 p-2.5 max-h-[70dvh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-1.5 mb-2 text-center">
            <div className="bg-slate-50 rounded-md p-1.5">
              <div className="text-[10px] text-slate-500">Элементов</div>
              <div className="text-xs font-semibold text-slate-900">{itemsCount}</div>
            </div>
            <div className="bg-slate-50 rounded-md p-1.5">
              <div className="text-[10px] text-slate-500">Площадь</div>
              <div className="text-xs font-semibold text-slate-900">{plotArea.toFixed(1)} м²</div>
            </div>
            <div className="bg-slate-50 rounded-md p-1.5">
              <div className="text-[10px] text-slate-500">Периметр</div>
              <div className="text-xs font-semibold text-slate-900">{perimeterM.toFixed(1)} м</div>
            </div>
          </div>

          <div className="space-y-1">
            {selectedFoundation && (
              <BreakdownRow badge="Слой 1" badgeClass="bg-amber-100 text-amber-700" name={selectedFoundation.name_ru} price={Math.round((selectedFoundation.price || 0) * plotArea)} />
            )}
            {selectedFloor && (
              <BreakdownRow badge="Слой 2" badgeClass="bg-blue-100 text-blue-700" name={selectedFloor.name_ru} price={Math.round((selectedFloor.price || 0) * plotArea)} />
            )}
            {selectedBorder && (
              <BreakdownRow badge="Бордюр" badgeClass="bg-purple-100 text-purple-700" name={selectedBorder.name_ru} price={getBorderPriceForBreakdown(selectedBorder, perimeterM)} />
            )}
            {selectedFence && (
              <BreakdownRow badge="Оградка" badgeClass={CATEGORY_BADGE.fence} name={selectedFence.name_ru} price={Math.round((selectedFence.price || 0) * perimeterM)} />
            )}
            {placedElements.map((el) => {
              const cat = categories?.find(c => c.id === el.category_id);
              const catName = cat?.name || '';
              const badgeClass = CATEGORY_BADGE[catName] || 'bg-slate-100 text-slate-700';
              return (
                <BreakdownRow
                  key={el.id}
                  badge={cat?.name_ru || '—'}
                  badgeClass={badgeClass}
                  name={el.name_ru || el.name || ''}
                  price={getElementPriceForBreakdown(el)}
                />
              );
            })}
            {flowerbedFilling && (() => {
              const innerPrice = placedElements.reduce((sum, el) => {
                if (el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle') {
                  const innerW = Math.max(0, (el.width || 1) - 0.16);
                  const innerD = Math.max(0, (el.depth || 1) - 0.08);
                  return sum + (Number(flowerbedFilling.price) || 0) * innerW * innerD;
                }
                return sum;
              }, 0);
              if (innerPrice <= 0) return null;
              return <BreakdownRow badge="Наполнение" badgeClass="bg-lime-100 text-lime-700" name={flowerbedFilling.name_ru} price={Math.round(innerPrice)} />;
            })()}
            {customItems?.map((it) => (
              <BreakdownRow key={it.id} badge="Доп." badgeClass="bg-slate-100 text-slate-700" name={it.name} price={Math.round(it.price || 0)} />
            ))}
            {!isPamyatniki && installationBreakdown.length > 0 && (
              <div className="rounded-md border border-slate-100 bg-white overflow-hidden mt-1">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 transition-colors"
                  onClick={() => setInstallationExpanded(v => !v)}
                >
                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 shrink-0">Монтаж</span>
                  <span className="text-slate-700 text-xs flex-1 truncate text-left">Монтажные работы</span>
                  <span className="text-slate-900 text-xs font-semibold whitespace-nowrap">{formatPriceKZ(installationVisibleTotal)} ₸</span>
                  <span className="text-[11px] text-slate-500 font-medium flex items-center gap-0.5 whitespace-nowrap">
                    {installationExpanded ? 'Скрыть' : 'Подробнее'}
                    <ChevronDown className={`h-3 w-3 transition-transform ${installationExpanded ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {installationExpanded && (
                  <div className="border-t border-slate-100 px-2 py-1.5 space-y-1">
                    {installationBreakdown.map((item) => {
                      const excluded = excludedInstallItems.has(item.label);
                      return (
                        <div key={item.label} className={`flex items-center gap-2 text-[11px] ${excluded ? 'opacity-40 line-through' : 'text-slate-600'}`}>
                          <span className="flex-1 truncate">{item.label}{item.count > 1 ? ` × ${item.count}` : ''}</span>
                          <span className="text-slate-700 font-medium whitespace-nowrap">{formatPriceKZ(item.price)} ₸</span>
                          <button
                            type="button"
                            onClick={() => setExcludedInstallItems(prev => {
                              const next = new Set(prev);
                              if (next.has(item.label)) next.delete(item.label); else next.add(item.label);
                              return next;
                            })}
                            className="text-slate-400 hover:text-red-500 transition-colors shrink-0"
                            title={excluded ? 'Вернуть' : 'Убрать'}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-700">Итого</span>
            <span className="font-bold text-slate-900">{formatPriceKZ(adjustedTotal)} ₸</span>
          </div>
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ badge, badgeClass, name, price }) {
  return (
    <div className="flex items-center gap-2 rounded-md px-1.5 py-1.5 hover:bg-slate-50">
      <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold shrink-0 ${badgeClass}`}>{badge}</span>
      <span className="text-slate-700 text-xs flex-1 truncate">{name}</span>
      <span className="text-slate-900 text-xs font-semibold whitespace-nowrap">{formatPriceKZ(price)} ₸</span>
    </div>
  );
}

export default function DesignerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  
  const {
    setPerimeterSize,
    setSelectedFloor,
    setSelectedFoundation,
    setSelectedFence,
    setSelectedBorder,
    setSharedView,
    selectedPlacedId,
    setSelectedPlacedId,
    removePlacedElement,
    mobileSidebarOpen,
    embedMode,
    setEmbedMode,
  } = useStore();

  // Quiz integration: catalog snapshot used to translate quiz answers → scene state.
  const stonesRef = useRef([]);
  const pendingQuizRef = useRef(null);

  const tryApplyPendingQuiz = useCallback(() => {
    const quiz = pendingQuizRef.current;
    if (!quiz) return;
    const { categories, elements } = useStore.getState();
    if (!categories.length || !elements.length || !stonesRef.current.length) return;
    applyQuizState(quiz, {
      categories,
      elements,
      stones: stonesRef.current,
    });
    pendingQuizRef.current = null;
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Embed mode + initial quiz read from URL (one-time on mount)
  useEffect(() => {
    if (isEmbedMode()) setEmbedMode(true);
    const initial = readQuizFromURL();
    if (initial) pendingQuizRef.current = initial;
  }, [setEmbedMode]);

  // Load catalog so quiz answers can be mapped to real elements.
  // Sidebar also loads it; in embed mode (no sidebar) we still need it here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { categories, elements } = useStore.getState();
      const needCatalog = !categories.length || !elements.length;
      try {
        if (needCatalog) {
          try { await seedData(); } catch {}
          const [catRes, elemRes] = await Promise.all([
            getCategories(VARIANT),
            getElements(undefined, false, VARIANT),
          ]);
          if (cancelled) return;
          useStore.setState({
            categories: catRes.data || [],
            elements: elemRes.data || [],
          });
        }
        const stonesRes = await getStones(true);
        if (cancelled) return;
        stonesRef.current = stonesRes.data || [];
        tryApplyPendingQuiz();
      } catch (e) {
        console.error('Catalog load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [tryApplyPendingQuiz]);

  // Listen for postMessage updates from quiz iframe parent
  useEffect(() => {
    const handler = (ev) => {
      if (!ev.data || ev.data.type !== 'quiz:update') return;
      pendingQuizRef.current = ev.data.payload || {};
      tryApplyPendingQuiz();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [tryApplyPendingQuiz]);

  // Удаление выбранного объекта по Delete / Backspace
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (!selectedPlacedId) return;
      e.preventDefault();
      removePlacedElement(selectedPlacedId);
      setSelectedPlacedId(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPlacedId, removePlacedElement, setSelectedPlacedId]);

  const loadSharedProject = useCallback(async (projectId) => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await getSharedProject(projectId);
      const project = response.data;

      // Set shared view mode - collapse menus
      setSharedView(true);

      // Restore perimeter size
      setPerimeterSize(project.perimeter_width, project.perimeter_length);

      // Слои будут восстановлены ниже с актуальными ценами (после загрузки каталога)
      setSelectedFoundation(null);
      setSelectedFloor(null);
      setSelectedFence(null);
      setSelectedBorder(null);

      // Подтягиваем актуальные цены из каталога
      const [elementsRes, stonesRes] = await Promise.all([
        getElements(undefined, false, VARIANT),
        getStones(true),
      ]);
      const catalogElements = elementsRes.data || [];
      const catalogStones = stonesRes.data || [];
      const elementById = Object.fromEntries(catalogElements.map(e => [e.id, e]));
      const stoneByName = Object.fromEntries(catalogStones.map(s => [s.name_ru, s]));

      // Вспомогательная функция: актуальная цена для placed-элемента
      const refreshPrice = (el) => {
        const catalog = elementById[el.element_id];
        if (!catalog) return el;
        if (el.price_unit === 'cubic_meter' && el.colorName) {
          // Памятник/тумба/цветник: цена из камня
          const stone = stoneByName[el.colorName];
          if (stone) return { ...el, price: stone.price_per_cubic_meter };
        }
        // Обычный элемент: берём price из каталога
        return { ...el, price: catalog.price, price_unit: catalog.price_unit || el.price_unit };
      };

      // Вспомогательная функция: актуальная цена для слоя (floor/foundation/fence/border)
      const refreshLayer = (layer) => {
        if (!layer) return layer;
        const catalog = elementById[layer.id];
        return catalog ? { ...layer, price: catalog.price } : layer;
      };

      // Restore placed elements with refreshed prices
      const elementsToRestore = (project.placed_elements || []).map(refreshPrice);
      useStore.setState({ placedElements: elementsToRestore });

      // Restore custom items
      const customItemsToRestore = project.custom_items || [];
      useStore.setState({ customItems: customItemsToRestore });

      // Обновляем цены слоёв
      if (project.selected_foundation) setSelectedFoundation(refreshLayer(project.selected_foundation));
      if (project.selected_floor) setSelectedFloor(refreshLayer(project.selected_floor));
      if (project.selected_fence) setSelectedFence(refreshLayer(project.selected_fence));
      if (project.selected_border) setSelectedBorder(refreshLayer(project.selected_border));

      // Clear the URL parameter after loading
      setSearchParams({});

    } catch (error) {
      console.error('Failed to load shared project:', error);
      setLoadError('Проект не найден');
      setTimeout(() => setLoadError(null), 3000);
    } finally {
      setLoading(false);
    }
  }, [
    setSearchParams,
    setSharedView,
    setPerimeterSize,
    setSelectedFoundation,
    setSelectedFloor,
    setSelectedFence,
    setSelectedBorder,
  ]);

  // Загрузка шаринга: только ПОСЛЕ zustand-persist (иначе localStorage перезапишет оградку/слои с прошлого визита)
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (!projectId) return;

    let cancelled = false;

    const run = () => {
      if (cancelled) return;
      // Сразу очищаем состояние из localStorage, чтобы сцена не рисовала старые объекты
      // пока идёт загрузка проекта с сервера
      useStore.setState({
        selectedFence: null,
        selectedFoundation: null,
        selectedFloor: null,
        selectedBorder: null,
        placedElements: [],
        customItems: [],
      });
      void loadSharedProject(projectId);
    };

    const persistApi = useStore.persist;
    let unsubPersist = () => {};

    if (persistApi.hasHydrated()) {
      run();
    } else {
      unsubPersist = persistApi.onFinishHydration(() => {
        run();
      });
    }

    return () => {
      cancelled = true;
      unsubPersist();
    };
  }, [searchParams, loadSharedProject]);

  // Embed mode: only the 3D scene + price chip. Used by quiz iframe.
  if (embedMode) {
    return (
      <div className="h-dvh w-full relative overflow-hidden" data-testid="designer-page-embed">
        <Scene3D />
        <EmbedPriceChip />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="h-dvh w-full flex flex-col" data-testid="designer-page">
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 z-50 bg-white/80 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
              <p className="text-slate-600">Загрузка проекта...</p>
            </div>
          </div>
        )}

        {/* Error toast */}
        {loadError && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
            {loadError}
          </div>
        )}

        {/* 3D Scene - fixed height top section */}
        <div className={`${mobileSidebarOpen ? 'h-[40dvh]' : 'flex-1'} w-full relative overflow-hidden transition-[height] duration-300`}>
          <Scene3D />

          {/* UI Overlay on the scene */}
          <div className="absolute inset-0 z-10 pointer-events-none">
            {isPamyatniki && (
              <button
                onClick={() => { window.location.href = '/kp-quiz.html?v=' + Date.now(); }}
                className="pointer-events-auto absolute left-2 top-2 md:left-1/2 md:-translate-x-1/2 z-20 h-11 px-4 rounded-xl shadow-lg bg-primary text-white font-semibold text-sm flex items-center gap-2 hover:bg-primary/90 active:scale-95 transition-transform"
                data-testid="open-kp-quiz"
              >
                <ClipboardList className="h-4 w-4" /> Квиз
              </button>
            )}
            <CostCalculator />
            <RotationToolbar />
            <BottomToolbar />
          </div>
        </div>

        {/* Sidebar - below the scene, not overlapping */}
        <Sidebar />
      </div>
    );
  }

  return (
    <div className="h-dvh w-full relative overflow-hidden" data-testid="designer-page">
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 bg-white/80 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
            <p className="text-slate-600">Загрузка проекта...</p>
          </div>
        </div>
      )}

      {/* Error toast */}
      {loadError && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
          {loadError}
        </div>
      )}

      {/* 3D Scene */}
      <Scene3D />

      {/* UI Overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {/* Left Sidebar - Element selection */}
        <Sidebar />

        {/* Кнопка входа в режим специалиста (сборка КП как квиз) — только pamyatniki */}
        {isPamyatniki && (
          <button
            onClick={() => { window.location.href = '/kp-quiz.html?v=' + Date.now(); }}
            className="pointer-events-auto absolute left-2 top-2 md:left-1/2 md:-translate-x-1/2 z-20 h-11 px-4 rounded-xl shadow-lg bg-primary text-white font-semibold text-sm flex items-center gap-2 hover:bg-primary/90 active:scale-95 transition-transform"
            data-testid="open-kp-quiz"
          >
            <ClipboardList className="h-4 w-4" /> Квиз
          </button>
        )}

        {/* Right Panel - Cost calculator */}
        <CostCalculator />

        {/* Bottom Rotation Toolbar - appears when element is selected */}
        <RotationToolbar />

        {/* Нижняя панель: камера + кнопки «Выбрать»/«Отмена» для памятника */}
        <BottomToolbar />
      </div>
    </div>
  );
}
